import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Plus, Edit2, X, Mail, Phone, Loader2, RefreshCw, ExternalLink, UserX, UserCheck } from 'lucide-react'
import { teachersApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'

const emptyForm = {
  firstName: '', lastName: '', email: '', mobile: '',
  gender: 'Male', password: '',
}

const getTeacherName = (t) =>
  t.name || `${t.firstName || ''} ${t.lastName || ''}`.trim() || '—'

function CardSkeleton() {
  return (
    <div className="card p-5 animate-pulse space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="space-y-1.5 flex-1">
          <div className="h-3 bg-gray-100 rounded w-2/3" />
          <div className="h-2 bg-gray-100 rounded w-1/3" />
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="h-2 bg-gray-100 rounded w-full" />
        <div className="h-2 bg-gray-100 rounded w-3/4" />
      </div>
      <div className="h-8 bg-gray-100 rounded" />
    </div>
  )
}

export default function TeachersPage() {
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [statusTarget, setStatusTarget] = useState(null)
  const [togglingStatus, setTogglingStatus] = useState(false)
  const [statusFilter, setStatusFilter] = useState('active')

  const fetchTeachers = async () => {
    try {
      setLoading(true)
      const data = await teachersApi.getAll()
      setTeachers(Array.isArray(data) ? data : data?.users ?? data?.teachers ?? [])
    } catch {
      toast.error('Failed to load teachers')
      setTeachers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchTeachers() }, [])

  const filtered = teachers.filter((t) => {
    const q = search.toLowerCase()
    const name = getTeacherName(t).toLowerCase()
    const matchSearch = (
      name.includes(q) ||
      (t.email || '').toLowerCase().includes(q) ||
      (t.mobile || '').toLowerCase().includes(q)
    )
    const matchStatus = statusFilter === 'all' || (t.accountStatus || 'active') === statusFilter
    return matchSearch && matchStatus
  })

  const openAdd = () => { setEditing(null); setForm(emptyForm); setModalOpen(true) }

  const openEdit = (t) => {
    setEditing(t._id || t.id)
    setForm({
      firstName: t.firstName || '',
      lastName: t.lastName || '',
      email: t.email || '',
      mobile: t.mobile || '',
      gender: t.gender || 'Male',
      password: '',
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.firstName.trim()) { toast.error('First name is required'); return }
    if (!editing && !form.password.trim()) { toast.error('Password is required'); return }
    try {
      setSaving(true)
      if (editing) {
        const { password, ...updatePayload } = form
        const updated = await teachersApi.update(editing, updatePayload)
        setTeachers((prev) => prev.map((t) => (t._id || t.id) === editing ? { ...t, ...updated } : t))
        toast.success('Teacher updated')
      } else {
        const created = await teachersApi.create({ ...form, role: 'teacher' })
        setTeachers((prev) => [...prev, created])
        toast.success('Teacher added')
      }
      setModalOpen(false)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save teacher')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleStatus = async () => {
    if (!statusTarget) return
    const nextStatus = statusTarget.currentStatus === 'inactive' ? 'active' : 'inactive'
    try {
      setTogglingStatus(true)
      const updated = await teachersApi.updateAccountStatus(statusTarget.id, nextStatus)
      setTeachers((prev) => prev.map((t) =>
        (t._id || t.id) === statusTarget.id ? { ...t, ...updated, accountStatus: nextStatus } : t
      ))
      toast.success(`Teacher ${nextStatus === 'inactive' ? 'deactivated' : 'reactivated'}`)
      setStatusTarget(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status')
    } finally {
      setTogglingStatus(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Teachers</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? '...' : `${teachers.filter(t => (t.accountStatus || 'active') === 'active').length} active · ${teachers.filter(t => t.accountStatus === 'inactive').length} inactive`}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button onClick={fetchTeachers} className="btn-secondary p-2" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={openAdd} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Teacher
          </button>
        </div>
      </div>

      <div className="card p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex-1">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or mobile..."
            className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-field cursor-pointer"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading ? (
          [...Array(6)].map((_, i) => <CardSkeleton key={i} />)
        ) : filtered.length === 0 ? (
          <p className="text-gray-400 text-sm col-span-full text-center py-10">No teachers found</p>
        ) : filtered.map((t) => {
          const displayName = getTeacherName(t)
          const isInactive = t.accountStatus === 'inactive'
          return (
            <div key={t._id || t.id} className={`card p-5 ${isInactive ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Avatar src={t.avatar} name={displayName} size="lg" />
                  <div>
                    <Link to={`/users/${t._id || t.id}`} className="font-semibold text-gray-900 text-sm hover:text-primary-700 transition-colors">
                      {displayName}
                    </Link>
                    <p className="text-xs text-gray-400">{t.gender || '—'}</p>
                    {isInactive && <span className="text-xs font-semibold text-red-500">Inactive</span>}
                  </div>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                  {t.role || 'teacher'}
                </span>
              </div>
              <div className="space-y-2 mb-4">
                {t.email && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Mail className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{t.email}</span>
                  </div>
                )}
                {t.mobile && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Phone className="w-3 h-3 flex-shrink-0" />
                    <span>{t.mobile}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <Link
                  to={`/users/${t._id || t.id}`}
                  className="btn-secondary flex-1 text-xs py-1.5 flex items-center justify-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Profile
                </Link>
                <button
                  onClick={() => openEdit(t)}
                  className="btn-secondary flex-1 text-xs py-1.5 flex items-center justify-center gap-1"
                >
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
                <button
                  onClick={() => setStatusTarget({ id: t._id || t.id, name: displayName, currentStatus: t.accountStatus || 'active' })}
                  className={`flex-1 text-xs py-1.5 rounded-lg border transition-colors flex items-center justify-center gap-1 ${isInactive ? 'border-green-200 text-green-600 hover:bg-green-50' : 'border-red-200 text-red-500 hover:bg-red-50'}`}
                >
                  {isInactive ? <><UserCheck className="w-3 h-3" /> Reactivate</> : <><UserX className="w-3 h-3" /> Deactivate</>}
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">{editing ? 'Edit Teacher' : 'Add Teacher'}</h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">First Name *</label>
                <input
                  className="input-field w-full"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name</label>
                <input
                  className="input-field w-full"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  placeholder="Last name"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                <input
                  className="input-field w-full"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@school.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Mobile</label>
                <input
                  className="input-field w-full"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  placeholder="10-digit number"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Gender</label>
                <select
                  className="input-field w-full"
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                >
                  <option>Male</option>
                  <option>Female</option>
                </select>
              </div>
              {!editing && (
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Password *</label>
                  <input
                    type="password"
                    className="input-field w-full"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Set initial password"
                  />
                </div>
              )}
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button onClick={() => setModalOpen(false)} className="btn-secondary" disabled={saving}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'Update' : 'Add Teacher'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate / Deactivate Confirm */}
      {statusTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${statusTarget.currentStatus === 'inactive' ? 'bg-green-50' : 'bg-red-50'}`}>
              {statusTarget.currentStatus === 'inactive'
                ? <UserCheck className="w-5 h-5 text-green-500" />
                : <UserX className="w-5 h-5 text-red-500" />
              }
            </div>
            <h3 className="font-bold text-gray-900 mb-1">
              {statusTarget.currentStatus === 'inactive' ? 'Reactivate' : 'Deactivate'} Teacher?
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {statusTarget.currentStatus === 'inactive'
                ? `${statusTarget.name} will be able to log in again.`
                : `${statusTarget.name} will lose access but data will be preserved.`
              }
            </p>
            <div className="flex gap-3">
              <button onClick={() => setStatusTarget(null)} className="btn-secondary flex-1" disabled={togglingStatus}>
                Cancel
              </button>
              <button
                onClick={handleToggleStatus}
                disabled={togglingStatus}
                className={`flex-1 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${statusTarget.currentStatus === 'inactive' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
              >
                {togglingStatus && <Loader2 className="w-4 h-4 animate-spin" />}
                {statusTarget.currentStatus === 'inactive' ? 'Reactivate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
